import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { PageHero, SectionLabel } from "../components/site/Bits";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { api, formatApiErrorDetail } from "../lib/api";
import { BUSINESS } from "../data/content";

const HOURS = [
  { day: "Monday – Friday", time: "8:00am – 6:00pm" },
  { day: "Saturday", time: "9:00am – 5:00pm" },
  { day: "Sunday", time: "Emergency bookings only" },
];

const empty = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contacts", { ...form, type: "contact" });
      setDone(true);
      toast.success("Message sent! We'll get back to you soon.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        crumb="Contact Us"
        label="Contact Us"
        title="Let's get you booked in."
        subtitle="Questions, quotes or bookings — reach out however suits you best. We're always happy to help."
      />

      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid gap-12 lg:grid-cols-12">
          {/* Info */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <ContactRow icon={Phone} label="Phone" value={BUSINESS.phone} href={BUSINESS.phoneHref} testid="contact-phone" />
              <ContactRow icon={Mail} label="Email" value={BUSINESS.email} href={BUSINESS.emailHref} testid="contact-email" />
              <ContactRow icon={MapPin} label="Location" value={BUSINESS.location} testid="contact-location" />
            </div>

            <div className="rounded-2xl bg-mist p-8">
              <div className="flex items-center gap-2 mb-5">
                <Clock className="w-5 h-5 text-brandgreen" />
                <h3 className="font-heading font-bold text-navy text-xl tracking-tight">Business Hours</h3>
              </div>
              <ul className="divide-y divide-black/5">
                {HOURS.map((h) => (
                  <li key={h.day} className="flex items-center justify-between py-3">
                    <span className="text-navy font-medium">{h.day}</span>
                    <span className="text-muted-foreground text-sm text-right">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="overflow-hidden rounded-2xl border border-black/5 h-64" data-testid="contact-map">
              <iframe
                title="Mark Clean Services Ltd location"
                src="https://www.google.com/maps?q=Preston,Lancashire,UK&output=embed"
                className="w-full h-full grayscale"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-black/5 bg-white p-8 lg:p-10">
              <SectionLabel>Send a message</SectionLabel>
              <h2 className="mt-4 mb-8 font-heading font-extrabold text-navy text-3xl tracking-tighter">
                Drop us a line
              </h2>

              {done ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="contact-success"
                  className="text-center py-10"
                >
                  <CheckCircle2 className="w-14 h-14 text-brandgreen mx-auto" />
                  <h3 className="mt-6 font-heading font-extrabold text-navy text-2xl tracking-tight">Message sent!</h3>
                  <p className="mt-2 text-muted-foreground">Thanks for reaching out — we'll reply as soon as we can.</p>
                  <Button onClick={() => setDone(false)} className="mt-8 rounded-full bg-navy hover:bg-brandgreen px-8">
                    Send another
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-navy">Name <span className="text-brandgreen">*</span></Label>
                      <Input data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className="h-12" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-navy">Phone</Label>
                      <Input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="07..." className="h-12" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-navy">Email <span className="text-brandgreen">*</span></Label>
                    <Input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="h-12" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-navy">Message <span className="text-brandgreen">*</span></Label>
                    <Textarea data-testid="contact-message-input" value={form.message} onChange={set("message")} placeholder="How can we help?" rows={5} />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="w-full sm:w-auto rounded-full bg-brandgreen hover:bg-navy text-white h-14 px-10 text-base font-semibold"
                  >
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : "Send Message"}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-center gap-4 group">
      <span className="grid place-items-center w-12 h-12 rounded-xl bg-navy text-white group-hover:bg-brandgreen transition-colors duration-300 shrink-0">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <span className="block text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</span>
        <span className="block font-heading font-bold text-navy text-lg tracking-tight">{value}</span>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block">{inner}</a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}
